import classNames from "classnames";
import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { ModalComponent } from "..";
import { useEnvironment } from "../../../customHooks/useEnvironment";
import { Icon } from "../../icons/icon";
import { PageLoader } from "../../loaders/pageLoader";
import { EmptyState } from "../../states/emptyState";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

const getFileType = (url: string | File | undefined) => {
  if (!url) {
    return "unknown";
  }

  if (typeof url === "string") {
    const extension = url.split(".")?.pop()?.toLowerCase() ?? "";

    if (["jpg", "jpeg", "png", "gif", "bmp", "svg"].includes(extension)) {
      return "image";
    }

    if (["pdf"].includes(extension)) {
      return "pdf";
    }
  } else {
    if (url.type.includes("image")) {
      return "image";
    }

    if (url.type.includes("pdf")) {
      return "pdf";
    }
  }

  return "unknown";
};

export const ImageViewModal = ({
  modalOpen,
  onModalClose = () => {},
  src,
  file,
}: {
  modalOpen: boolean;
  onModalClose: () => void;
  src?: string;
  file?: File;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fileType, setFileType] = useState<"image" | "pdf" | "unknown">(
    "unknown"
  );
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);

  const { environment } = useEnvironment();

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setLoading(false);
    setNumPages(numPages);
  }

  useEffect(() => {
    if (!modalOpen) {
      setLoading(false);
    } else {
      const fType = getFileType(src ?? file);

      if (fType === "pdf") {
        setLoading(true);
      }

      setFileType(fType);
    }
    // eslint-disable-next-line
  }, [modalOpen]);

  return (
    <ModalComponent
      isModalOpen={modalOpen}
      onModalClose={() => {
        onModalClose();
      }}
      title={undefined}
      subTitle={undefined}
      childrenClassName="!mt-0"
      modalClassName="!min-h-[200px] !max-h-[90%] md:!max-w-[703px] !p-0 md:p-0 "
      hasButtons={false}
      showHeader={false}
      showMobileTopBar={false}
      isMenuModal={false}
    >
      <>
        <div
          className={classNames(
            "absolute z-10 flex items-center gap-6 top-6 right-8",
            {
              "!hidden": loading,
            }
          )}
        >
          {environment !== "mobile" ? (
            <button
              type="button"
              className="w-fit"
              onClick={() => {
                window.open(src, "_blank");
              }}
            >
              <Icon svg="maximize" width={24} height={24} />
            </button>
          ) : undefined}

          <button
            type="button"
            className="w-fit"
            onClick={() => {
              onModalClose();
            }}
          >
            <Icon svg="close-icon-2" width={24} height={24} />
          </button>
        </div>

        <div className="w-full overflow-x-scroll h-full relative flex items-center justify-center">
          {fileType === "image" && (
            <img
              src={file ? URL.createObjectURL(file) : src}
              className="!w-full !h-full overflow-scroll"
              alt="doc view"
            />
          )}

          {fileType === "pdf" ? (
            <Document
              file={file ? URL.createObjectURL(file) : src}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadStart={() => {
                setLoading(true);
              }}
              onLoadError={() => {
                setLoading(false);
              }}
              className={"h-fit w-full"}
              loading={<PageLoader />}
            >
              <Page pageNumber={pageNumber} />
            </Document>
          ) : undefined}

          {fileType === "unknown" && (
            <EmptyState
              title={"Nothing to see here"}
              subtitle={
                "Ooops! This could be due to an unsupported file type, or broken link."
              }
              className={"mt-32"}
            />
          )}
        </div>

        {fileType === "pdf" && !loading ? (
          <div className="absolute p-4 z-10 flex items-center justify-between gap-6 bottom-0 left-0 right-0">
            <button
              type="button"
              className="w-fit"
              onClick={() => {
                setPageNumber(pageNumber - 1);
              }}
              disabled={pageNumber === 1}
            >
              <Icon
                svg="caret-down"
                width={24}
                height={24}
                className="rotate-90"
              />
            </button>

            <p className="text-sm font-bold bg-pry-dark-green-400 text-white p-1 px-2 rounded-full">
              Page {pageNumber} of {numPages}
            </p>

            <button
              type="button"
              className="w-fit"
              onClick={() => {
                setPageNumber(pageNumber + 1);
              }}
              disabled={pageNumber === numPages}
            >
              <Icon
                svg="caret-down"
                width={24}
                height={24}
                className="-rotate-90"
              />
            </button>
          </div>
        ) : undefined}
      </>
    </ModalComponent>
  );
};
